import {useStores} from "~/stores";
import {notificationStore} from "~/stores/init";
import {m_taxonomy} from "~/services/classes/TaxonomyClasses";
import {useCanEditTaxonomy} from "~/pages/taxonomy-builder-v2/hooks/useCanEditTaxonomy";
import {useStoreTaxonomyStateMutation} from "~/pages/taxonomy-builder-v2/hooks/useStoreTaxonomyStateMutation";
import {
    addCategory,
    mergeCategories,
    moveCategory,
    removeCategory,
    renameCategory,
} from "~/pages/taxonomy-builder-v2/services/taxonomyTreeEdits";

function findParent(node: m_taxonomy.Tree, id: number): m_taxonomy.Tree | undefined {
    for (const child of node.children) {
        if (child.id === id) return node;
        const found = findParent(child, id);
        if (found) return found;
    }
    return undefined;
}

/**
 * Add, rename, move, merge and delete on the tree view. Each change saves the whole tree straight away
 * (`store_state`), like the current builder. Call from an `observer` component.
 */
export function useTaxonomyTreeEditing(taxonomy: m_taxonomy.FullSerializer) {
    const {authStore} = useStores();
    const canEdit = useCanEditTaxonomy(taxonomy);
    const {mutate, isPending} = useStoreTaxonomyStateMutation();

    /** Adds `name` under `parentId`; `onSaved` gets the new category's id. */
    const add = (parentId: number, name: string, description: string, onSaved: (id: number) => void) => {
        const id = taxonomy.next_node_id;
        mutate({
            taxonomy,
            operation: m_taxonomy.Operation.Add,
            state: addCategory(taxonomy.diff_tree, parentId, {id, label: name, description}, authStore.getUserId()),
            successMessage: `${name} added.`,
        }, {onSuccess: () => onSaved(id)});
    };

    /** Deletes the category with its whole branch. */
    const remove = (id: number, label: string, onSaved: () => void) => {
        mutate({
            taxonomy,
            operation: m_taxonomy.Operation.Delete,
            state: removeCategory(taxonomy.diff_tree, id, authStore.getUserId()),
            successMessage: `${label} deleted.`,
        }, {onSuccess: onSaved});
    };

    /**
     * Renames the category, with its (optional) description, unless a sibling already has the name (the
     * current builder's check; empty names are dropped by `required` on ClickToEdit). Saved as an Update,
     * like the current builder's "Update category"; the backend reports it as `renamed`.
     */
    const rename = (id: number, from: string, to: string, description: string, onSaved: () => void) => {
        const label = to.trim();
        const fail = (message: string) => notificationStore.show(message, {
            severity: 'error',
            id: `taxonomy-rename-error-${id}`,
            autoHideDuration: 4000,
        });
        if (!label) return;
        const parent = findParent(taxonomy.diff_tree, id);
        if (label === from) {
            // Same name: only the description can have changed. Nothing to save closes the dialog.
            const current = parent?.children.find(c => c.id === id)?.values.description ?? '';
            if (description === current) {
                onSaved();
                return;
            }
            mutate({
                taxonomy,
                operation: m_taxonomy.Operation.Update,
                state: renameCategory(taxonomy.diff_tree, id, label, description, authStore.getUserId()),
                successMessage: `${label} description updated.`,
            }, {onSuccess: onSaved});
            return;
        }
        const taken = parent?.children.some(c => c.id !== id && !c.removed && c.label.toLowerCase() === label.toLowerCase());
        if (taken) {
            fail(`A category named "${label}" already exists under ${parent?.id === taxonomy.diff_tree.id ? 'L1' : parent?.label}.`);
            return;
        }
        mutate({
            taxonomy,
            operation: m_taxonomy.Operation.Update,
            state: renameCategory(taxonomy.diff_tree, id, label, description, authStore.getUserId()),
            successMessage: `${from} renamed to ${label}.`,
        }, {onSuccess: onSaved});
    };

    /** Moves the category, with its whole branch, under `toId` (the drop target was checked while dragging). */
    const move = (id: number, label: string, toId: number, toLabel: string, onSaved: () => void) => {
        mutate({
            taxonomy,
            operation: m_taxonomy.Operation.Move,
            state: moveCategory(taxonomy.diff_tree, id, toId, authStore.getUserId()),
            successMessage: `${label} moved to ${toLabel}.`,
        }, {onSuccess: onSaved});
    };

    /** Merges sibling categories (under `parentId`) into a new one named `name`. */
    const merge = (parentId: number, ids: readonly number[], name: string, description: string, onSaved: (id: number) => void) => {
        const id = taxonomy.next_node_id;
        mutate({
            taxonomy,
            operation: m_taxonomy.Operation.Merge,
            state: mergeCategories(taxonomy.diff_tree, parentId, ids, {id, label: name, description}, authStore.getUserId()),
            successMessage: `${ids.length} categories merged into ${name}.`,
        }, {onSuccess: () => onSaved(id)});
    };

    return {canEdit, isSaving: isPending, add, remove, rename, move, merge};
}
